import { DataLoader } from 'src/app/services/data-loader/data-loader';
import { DataLoaderResult } from 'src/app/services/data-loader/data-loader-result';
import { standardRun } from 'src/app/services/data-loader/utils/standard-run';
import { QueryContextSettingID } from 'src/app/services/query-context/models/IQueryContext';
import { Factory } from 'src/app/utils/Factory';
import { ICreatedInstanceId, IQueryCacheService } from 'src/app/utils/IFactory';
import { generateTableFullPath } from 'src/app/utils/datasource-sql-map/datasource';
import { bq2echartsformat } from 'src/app/utils/echarts/echarts';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { BiVizDashboardMasterRevenueByDayNoSqlMap } from '../sql-maps/biviz-dashboard-master-revenue-by-day-no';

export interface CohortRevenueDailyAggDataLoaderFactoryOptions
    extends ICreatedInstanceId,
        IQueryCacheService {
    gcpProjectId: GcpProjectId | (() => GcpProjectId);
}

export class CohortRevenueDailyAggDataLoaderFactory extends Factory<
    DataLoader,
    CohortRevenueDailyAggDataLoaderFactoryOptions
> {
    override FACTORY_CLASS: string = 'CohortRevenueDailyAggDataLoaderFactory';
    /** This function returns DataLoader instance for analytics vs store reports differences */

    getInstance(options: CohortRevenueDailyAggDataLoaderFactoryOptions) {
        // let options = verifyRequiredOptions(rawOptions);
        // if (!verifyRequiredOptions(options)) {
        //     throw Error(
        //         '[getRetentionByFirstOpenDateDataLoaderFactory] Provided options are of type SalesDatetimeDataLoaderOptions'
        //     );
        // }
        return new DataLoader(options.createdInstanceId, async function (
            queryContext,
            cancellationToken
        ) {
            const resolvedGcpProjectId: GcpProjectId =
                typeof options.gcpProjectId === 'function'
                    ? options.gcpProjectId()
                    : options.gcpProjectId;
            const process = async () => {
                this.emitter.emit(
                    DataLoaderResult.getLoading(this.latestData, queryContext)
                );

                const datasourceSqlMap =
                    BiVizDashboardMasterRevenueByDayNoSqlMap.sqlMap;

                // identify columns not supported in datasource
                const dsColumns = DataLoader.separatePresentAndMissingDims(
                    queryContext.datasourceColumns,
                    datasourceSqlMap.columns
                );

                const { pendingDataLoaderResultStatus, pendingStatusMessage } =
                    DataLoader.getMissingColumnsStatus(dsColumns.missing);

                const { splitSelect, splitGroupBy, where, windowRunning } =
                    BiVizDashboardMasterRevenueByDayNoSqlMap.compileSQLStatements(
                        dsColumns.present,
                        queryContext.settings,

                        { gcpProjectId: resolvedGcpProjectId },
                        {
                            // where: [`date >= date_dim.days_ago_30`],
                        }
                    );
                const tableFullPath = `${generateTableFullPath(
                    resolvedGcpProjectId,
                    datasourceSqlMap.dataset,
                    datasourceSqlMap.tableId
                )}`;

                const usd_pln =
                    queryContext.settings[QueryContextSettingID.CURRENCY] ??
                    'pln';

                const query = `
-- DataLoaderId: ${this.instanceId}

WITH BASE AS
(
  SELECT ${splitSelect}day_no
    , SUM(revenue_net_${usd_pln}) / SUM(cohort_size) AS ARPU_s_net_${usd_pln}
    , SUM(revenue_gross_${usd_pln}) / SUM(cohort_size) AS ARPU_s_gross_${usd_pln}
    , SUM(inapp_revenue_net_${usd_pln}) / SUM(cohort_size) AS inapp_ARPU_s_net_${usd_pln}
    , SUM(inapp_revenue_gross_${usd_pln}) / SUM(cohort_size) AS inapp_ARPU_s_gross_${usd_pln}
    , SUM(ad_revenue_net_${usd_pln}) / SUM(cohort_size) AS ad_ARPU_s_net_${usd_pln}
    , SUM(ad_revenue_gross_${usd_pln}) / SUM(cohort_size) AS ad_ARPU_s_gross_${usd_pln}
  FROM \`${tableFullPath}\`
  ${where}
  
  GROUP BY ${splitGroupBy}day_no
  ORDER BY day_no
)

SELECT ${splitGroupBy}day_no
  , SUM(ARPU_s_net_${usd_pln}) OVER (${windowRunning}) AS ARPU_net_${usd_pln}
  , SUM(ARPU_s_gross_${usd_pln}) OVER (${windowRunning}) AS ARPU_gross_${usd_pln}
  , SUM(inapp_ARPU_s_net_${usd_pln}) OVER (${windowRunning}) AS inapp_ARPU_net_${usd_pln}
  , SUM(inapp_ARPU_s_gross_${usd_pln}) OVER (${windowRunning}) AS inapp_ARPU_gross_${usd_pln}
  , SUM(ad_ARPU_s_net_${usd_pln}) OVER (${windowRunning}) AS ad_ARPU_net_${usd_pln}
  , SUM(ad_ARPU_s_gross_${usd_pln}) OVER (${windowRunning}) AS ad_ARPU_gross_${usd_pln}
FROM BASE
ORDER BY ${splitGroupBy}day_no
`;

                const queryResponse = await DataLoader.runQuery(
                    query,
                    options.queryCacheService
                );
                cancellationToken.throwIfCancellationRequested();
                var data = [...queryResponse.result];
                bq2echartsformat(data);

                this.emitter.emit({
                    data,
                    queryContext,
                    status: pendingDataLoaderResultStatus,
                    statusMessage: pendingStatusMessage,
                    isLoading: false,
                    sqlAndJsProcessing: query,
                });
            };

            await standardRun(this, queryContext, process);
        });
    }
}
